import React, { FC, useEffect } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import './eventDetailsPage.module.scss';
import { Page } from '../../../mainv3/components/atoms/layout/Page';
import { PageBody } from '../../../mainv3/components/atoms/layout/PageBody';
import { EyeCatch } from './components/EyeCatch';
import { EventDescription } from './components/EventDescription';
import { DetailsPageState } from './states/DetailsPageState';
import { NotFoundPage } from '../common/NotFoundPage';
import { GlobalLoader } from '../../components/GlobalLoader';
import { CoachProfile } from '../common/coach/CoachProfile';
import { EventDetailsSection } from './components/EventDetailsSection';
import { FloatingEventInfo } from './components/FloatingEventInfo';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import { useNavigation } from '../../../mainv3/components/hocs/withNavigation';
import { Service } from '../../../commonv3/types/service';
import { NavigationSpecs } from '../../../mainv3/states/NavigationState';
import { FooterSpecs, useFooter } from '../../../mainv3/states/FooterState';
import { RouteComponentProps } from 'react-router-dom';
import { MarkdownText } from '../common/text/MarkdownText';
import { FlagKeys } from '../../states/CurrentUserState';

export function getEventNavigationType(service: Service) {
  switch (service) {
    case Service.BEAUTY:
      return NavigationSpecs.beauty();
    case Service.MONEY:
      return NavigationSpecs.money();
    case Service.LIKES:
      return GlobalStatesV3.currentUser.isSignedIn() ? NavigationSpecs.side() : NavigationSpecs.standard();
  }
}

export function getEventFooterType(service: Service) {
  switch (service) {
    case Service.BEAUTY:
      return FooterSpecs.beauty();
    case Service.MONEY:
      return FooterSpecs.money();
    case Service.LIKES:
      return FooterSpecs.likes();
  }
}

type Props = RouteComponentProps<{ eventId: string }>;

export const EventDetailsPage: FC<Props> = observer(({ match }: Props) => {
  const eventId = Number(match?.params.eventId);
  const pageState = useLocalStore(() => new DetailsPageState());
  const {
    event,
    eventStatus,
    userEventReservationStatus,
    membershipAvailable,
    eventWaitlistState,
    initialized,
    isIWS,
  } = pageState;
  useEffect(() => {
    pageState.init(eventId, GlobalStatesV3.currentUser);
  }, []);
  const service = event.map((e) => e.service).get();
  useNavigation(getEventNavigationType(service));
  useFooter(getEventFooterType(service));
  if (event.isNotFound()) {
    return <NotFoundPage entityType="イベント" />;
  }
  if (event.loading) {
    return <GlobalLoader />;
  }
  const evt = event.getOrThrow();
  // INNOVATIVE WOMEN'S SUMMIT のみ応募者数の表示をフラグ制御にする
  // https://sheinc.atlassian.net/browse/SHE-5091
  const hideReservationCount =
    isIWS && GlobalStatesV3.currentUser.hasEnabledFlagKey(FlagKeys.HIDE_RESERVATION_COUNT_FOR_IWS);

  return (
    <Page id="event-details-page" styleName="event-details-page">
      <PageBody styleName="event-details-page-body">
        {eventStatus && userEventReservationStatus && <EyeCatch event={evt} />}

        <div styleName="two-columns-container">
          <div styleName="right-column">
            {eventStatus && userEventReservationStatus && (
              <FloatingEventInfo
                event={evt}
                membershipAvailable={membershipAvailable}
                eventStatus={eventStatus}
                userEventReservationStatus={userEventReservationStatus}
                eventWaitlistState={eventWaitlistState}
                hideReservationCount={hideReservationCount}
                loading={!initialized}
              />
            )}
          </div>
          <div styleName="left-column">
            <EventDetailsSection id="overview" title="イベント概要">
              <EventDescription event={evt} />
            </EventDetailsSection>
            <EventDetailsSection id="matching-people" title="こんな方におすすめ">
              <div styleName="matching-people">
                <MarkdownText source={evt.target_description} />
              </div>
            </EventDetailsSection>
            {evt.coaches.length > 0 && (
              <EventDetailsSection id="coaches" title="講師紹介">
                {evt.coaches.map((coach) => (
                  <CoachProfile key={coach.id} coach={coach} />
                ))}
              </EventDetailsSection>
            )}
          </div>
        </div>
      </PageBody>
    </Page>
  );
});
